// Form.js — 郵便番号自動入力 / 数字キーボード / CF7 確認画面 / 予約希望日カレンダー

import { bindOnce } from '../utils/bindOnce';

const CONFIG = {
  formSelector: '.wpcf7 form',
  zipFieldName: 'zip',
  addressFieldName: 'address',
  privacyFieldName: 'privacy',
  privacyCheckedLabel: '同意する',
  privacyUncheckedLabel: '未同意',
  thanksUrl: '/thanks/',
  scrollOffsetFallback: 120,
  yubinbangoInterval: 50,
  yubinbangoTimeout: 10000,
  numInputSelector: '.js-num-input',
  morningNoteClass: 'c-form__preferenceNote js-reservation-morning-note',
};

// 郵便番号 → 住所（yubinbango Microformat）
function addAutoFunc() {
  const form = document.querySelector(CONFIG.formSelector);
  const zipInput = document.querySelector(`[name="${CONFIG.zipFieldName}"]`);
  const addInput = document.querySelector(`[name="${CONFIG.addressFieldName}"]`);

  if (!form || !zipInput || !addInput) return;
  if (!bindOnce(form, 'yubinbango')) return;

  form.classList.add('h-adr');

  if (!form.querySelector('.p-country-name')) {
    form.insertAdjacentHTML('afterbegin', '<input type="hidden" class="p-country-name" value="Japan">');
  }

  zipInput.classList.add('p-postal-code');

  // p-extended-address は付けない（後勝ちで上書きされる）
  addInput.classList.add('p-region', 'p-locality', 'p-street-address');

  waitForYubinBango((MicroformatDom) => new MicroformatDom());
}

function waitForYubinBango(callback) {
  const ready = () => window.YubinBango?.MicroformatDom;

  if (ready()) {
    callback(ready());
    return;
  }

  const deadline = Date.now() + CONFIG.yubinbangoTimeout;
  const timer = setInterval(() => {
    if (ready()) {
      clearInterval(timer);
      callback(ready());
      return;
    }
    if (Date.now() > deadline) clearInterval(timer);
  }, CONFIG.yubinbangoInterval);
}

// 数字入力欄に inputmode="numeric"
function numKeyFunc() {
  const inputs = document.querySelectorAll(CONFIG.numInputSelector);
  if (inputs.length === 0) return;

  inputs.forEach((el) => {
    el.setAttribute('inputmode', 'numeric');
  });
}

// CF7 確認画面フロー
function confirmForm() {
  const escapeName = (name) => (window.CSS?.escape ? window.CSS.escape(name) : String(name).replace(/["\\]/g, '\\$&'));

  const headerOffset = () => {
    const header = document.querySelector('.l-header');
    if (!header) return CONFIG.scrollOffsetFallback;
    const rect = header.getBoundingClientRect();
    return rect.height + parseFloat(getComputedStyle(header).top || 0) + 20;
  };

  const scrollToWithOffset = (target) => {
    if (!target) return;
    const top = window.scrollY + target.getBoundingClientRect().top - headerOffset();
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  };

  const normalizeName = (name) => String(name || '').replace(/\[\]$/, '');

  document.querySelectorAll(CONFIG.formSelector).forEach((form) => {
    const inputsWrap = form.querySelector('[data-inputs]');
    const confirmWrap = form.querySelector('[data-confirm]');
    const toConfirmBtn = form.querySelector('[data-to-confirm]');
    const backBtn = form.querySelector('[data-back]');
    const sendBtn = form.querySelector('[data-send]');
    const sendWrap = form.querySelector('[data-to-send]');
    const cf7Submits = form.querySelectorAll('.wpcf7-submit');

    if (!inputsWrap || !confirmWrap || !toConfirmBtn || !backBtn || !sendBtn || cf7Submits.length === 0) return;
    if (!bindOnce(form, 'cf7Confirm')) return;

    const hide = (el) => {
      if (!el) return;
      el.setAttribute('hidden', '');
      el.style.display = 'none';
    };

    const show = (el, display = '') => {
      if (!el) return;
      el.removeAttribute('hidden');
      el.style.display = display;
    };

    const disableSending = () => {
      sendBtn.disabled = true;
      sendBtn.setAttribute('aria-busy', 'true');
      cf7Submits.forEach((btn) => {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      });
    };

    const enableSending = () => {
      sendBtn.disabled = false;
      sendBtn.removeAttribute('aria-busy');
      cf7Submits.forEach((btn) => {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      });
    };

    // CF7 標準の [submit] は常に隠し、カスタムボタンで代替する。
    // スピナーは init 時に .has-spinner の直後へ後から挿入されるため、都度取得する。
    const hideCf7Submits = () => {
      cf7Submits.forEach((btn) => hide(btn));
      form.querySelectorAll('.wpcf7-spinner').forEach((spinner) => {
        spinner.style.display = 'none';
      });
    };

    // 初期状態: 入力画面と「確認する」のみ表示する
    const showInputStep = () => {
      show(inputsWrap);
      show(toConfirmBtn);
      hide(confirmWrap);
      hide(sendWrap);
      hideCf7Submits();
    };

    // 確認画面: 「戻る」「送信する」のみ表示する
    const showConfirmStep = () => {
      hide(inputsWrap);
      show(confirmWrap);
      hide(toConfirmBtn);
      show(sendWrap);
      hideCf7Submits();
    };

    showInputStep();

    const validateBeforeConfirm = () => {
      form.querySelectorAll('[name]').forEach((el) => el.setCustomValidity?.(''));

      const required = form.querySelectorAll('[name][required], [name][aria-required="true"], .wpcf7-validates-as-required [name]');

      const checkedGroups = new Set();
      let firstInvalid = null;

      required.forEach((el) => {
        const name = el.name;
        if (!name) return;

        if (el.type === 'checkbox' || el.type === 'radio') {
          if (checkedGroups.has(name)) return;
          checkedGroups.add(name);

          const group = form.querySelectorAll(`[name="${escapeName(name)}"]`);
          if (!Array.from(group).some((i) => i.checked)) {
            const head = group[0];
            head?.setCustomValidity?.('この項目は必須です');
            if (!firstInvalid) firstInvalid = head;
          }
        } else if (el.tagName === 'SELECT') {
          if (!el.value) {
            el.setCustomValidity('この項目は必須です');
            if (!firstInvalid) firstInvalid = el;
          }
        } else {
          if (!(el.value || '').trim()) {
            el.setCustomValidity('この項目は必須です');
            if (!firstInvalid) firstInvalid = el;
          }
        }
      });

      const isValid = form.reportValidity();
      if (!isValid && firstInvalid) {
        scrollToWithOffset(firstInvalid);
        firstInvalid.focus({ preventScroll: true });
      }
      return isValid;
    };

    const populateConfirm = () => {
      const processedNames = new Set();

      form.querySelectorAll('[name]').forEach((el) => {
        const name = el.name;
        if (!name) return;
        if (processedNames.has(name)) return;
        processedNames.add(name);

        const fieldName = normalizeName(name);
        let value = '';

        if (el.type === 'checkbox') {
          const checked = form.querySelectorAll(`input[name="${escapeName(name)}"]:checked`);
          if (fieldName === CONFIG.privacyFieldName) {
            value = checked.length ? CONFIG.privacyCheckedLabel : CONFIG.privacyUncheckedLabel;
          } else {
            value = Array.from(checked)
              .map((i) => i.value)
              .join(', ');
          }
        } else if (el.type === 'radio') {
          const checked = form.querySelector(`input[name="${escapeName(name)}"]:checked`);
          value = checked ? checked.value : '';
        } else if (el.tagName === 'SELECT') {
          value = el.value || '';
        } else {
          value = el.value ?? '';
        }

        const slot = form.querySelector(`[data-field="${escapeName(name)}"], [data-field="${escapeName(fieldName)}"]`);
        if (slot) slot.textContent = value;
      });
    };

    toConfirmBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (!validateBeforeConfirm()) return;

      populateConfirm();
      showConfirmStep();
      scrollToWithOffset(confirmWrap);
    });

    backBtn.addEventListener('click', (e) => {
      e.preventDefault();
      showInputStep();

      const firstInput = inputsWrap.querySelector('input, textarea, select');
      scrollToWithOffset(inputsWrap);
      firstInput?.focus({ preventScroll: true });
    });

    form.addEventListener('wpcf7beforesubmit', () => {
      disableSending();
    });

    const handleError = (apiResponse) => {
      if (!confirmWrap.hasAttribute('hidden')) backBtn.click();

      const msg = form.querySelector('.wpcf7-response-output')?.textContent?.trim();
      if (msg) {
        const errorBox = form.querySelector('[data-error]');
        if (errorBox) {
          errorBox.textContent = msg;
          errorBox.removeAttribute('hidden');
        }
      }

      const code = apiResponse?.code || apiResponse?.status;
      if (code === 'rest_invalid_nonce' || code === 'aborted') {
        try {
          window.wpcf7?.init?.(form);
        } catch {
          // ignore
        }
      }

      enableSending();

      const errorTarget = form.querySelector('.wpcf7-not-valid') || form.querySelector('[aria-invalid="true"]') || form.querySelector('.wpcf7-response-output');
      scrollToWithOffset(errorTarget);
      errorTarget?.focus?.({ preventScroll: true });
    };

    form.addEventListener('wpcf7invalid', (e) => handleError(e.detail?.apiResponse));
    form.addEventListener('wpcf7spam', (e) => handleError(e.detail?.apiResponse));
    form.addEventListener('wpcf7mailfailed', (e) => handleError(e.detail?.apiResponse));

    form.addEventListener('wpcf7submit', (e) => {
      const status = e.detail?.apiResponse?.status;

      if (status === 'mail_sent') {
        const thanksUrl = form.closest('[data-thanks]')?.dataset.thanks || CONFIG.thanksUrl;
        window.location.href = thanksUrl;
      } else {
        handleError(e.detail?.apiResponse);
      }

      enableSending();
    });

    form.addEventListener('reset', () => {
      if (!confirmWrap.hasAttribute('hidden')) backBtn.click();
      enableSending();
    });
  });
}

// 予約希望日（Flatpickr）+ 希望時間帯制御 — booking ページのみ config が渡る
function formatReservationYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isReservationClosedDate(date, rules) {
  const ymd = formatReservationYmd(date);
  if (rules.closedWeekdays.has(date.getDay())) return true;
  if (rules.specialClosedDates.has(ymd)) return true;
  return false;
}

function markHolidayDay(dayElem, date, rules) {
  if (!dayElem || !(date instanceof Date)) return;
  if (rules.holidays.has(formatReservationYmd(date))) {
    dayElem.classList.add('is-holiday');
  }
}

function isReservationMorningOnly(dateOrYmd, rules) {
  let weekday;
  let ymd;

  if (dateOrYmd instanceof Date) {
    weekday = dateOrYmd.getDay();
    ymd = formatReservationYmd(dateOrYmd);
  } else {
    const parts = String(dateOrYmd).split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return false;
    const [y, m, d] = parts;
    const local = new Date(y, m - 1, d);
    if (local.getFullYear() !== y || local.getMonth() !== m - 1 || local.getDate() !== d) return false;
    weekday = local.getDay();
    ymd = formatReservationYmd(local);
  }

  if (rules.morningOnlyWeekdays.has(weekday)) return true;
  if (rules.holidays.has(ymd)) return true;
  return false;
}

function syncReservationPeriod(periodSelect, noteEl, dateYmd, rules) {
  if (!periodSelect) return;

  const morningOnly = Boolean(dateYmd) && isReservationMorningOnly(dateYmd, rules);
  const afternoonValues = rules.afternoonPeriodValues;

  Array.from(periodSelect.options).forEach((opt) => {
    if (!opt.value) {
      opt.disabled = false;
      return;
    }

    if (afternoonValues.has(opt.value)) {
      opt.disabled = morningOnly;
      if (morningOnly && periodSelect.value === opt.value) {
        periodSelect.value = '';
        periodSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      opt.disabled = false;
    }
  });

  if (noteEl) {
    if (morningOnly) {
      noteEl.hidden = false;
      noteEl.removeAttribute('hidden');
    } else {
      noteEl.hidden = true;
      noteEl.setAttribute('hidden', '');
    }
  }
}

function ensureMorningNote(periodSelect, message) {
  const content = periodSelect.closest('.c-form__content');
  if (!content) return null;

  let note = content.querySelector('.js-reservation-morning-note');
  if (!note) {
    note = document.createElement('p');
    note.className = CONFIG.morningNoteClass;
    note.setAttribute('hidden', '');
    note.textContent = message;
    content.appendChild(note);
  }

  return note;
}

function resetReservationPair(fp, periodSelect, noteEl, rules) {
  if (fp) fp.clear();
  syncReservationPeriod(periodSelect, noteEl, '', rules);
}

async function reservationCalendarFunc() {
  const raw = window.newArtReservationCalendar;
  if (!raw || !Array.isArray(raw.dateFields) || raw.dateFields.length === 0) return;

  const selector = raw.dateFields.map((name) => `[name="${name}"]`).join(',');
  const dateInputs = document.querySelectorAll(selector);
  if (dateInputs.length === 0) return;

  const [{ default: flatpickr }, jaLocale] = await Promise.all([import('flatpickr'), import('flatpickr/dist/l10n/ja.js')]);
  await import('flatpickr/dist/flatpickr.min.css');

  const Japanese = jaLocale.Japanese || jaLocale.default?.Japanese || jaLocale.default;
  if (Japanese) flatpickr.localize(Japanese);

  const rules = {
    closedWeekdays: new Set((raw.closedWeekdays || []).map(Number)),
    specialClosedDates: new Set(raw.specialClosedDates || []),
    morningOnlyWeekdays: new Set((raw.morningOnlyWeekdays || []).map(Number)),
    holidays: new Set(raw.holidays || []),
    afternoonPeriodValues: new Set(raw.afternoonPeriodValues || ['午後']),
    periodFieldMap: raw.periodFieldMap || {},
    messages: raw.messages || {},
  };

  const instances = [];

  dateInputs.forEach((input) => {
    const form = input.closest(CONFIG.formSelector) || input.closest('form');
    if (!form) return;

    const dateName = input.getAttribute('name');
    const periodName = rules.periodFieldMap[dateName];
    const periodSelect = periodName ? form.querySelector(`[name="${periodName}"]`) : null;

    if (!bindOnce(input, 'reservationCalendar')) return;

    const noteEl = periodSelect ? ensureMorningNote(periodSelect, rules.messages.morningOnly || '土曜日・祝日は午前のみご希望いただけます。') : null;

    input.setAttribute('type', 'text');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('inputmode', 'none');
    input.readOnly = true;

    const minAttr = input.getAttribute('min');
    const maxAttr = input.getAttribute('max');

    const fp = flatpickr(input, {
      dateFormat: 'Y-m-d',
      allowInput: false,
      disableMobile: true,
      minDate: minAttr || undefined,
      maxDate: maxAttr || undefined,
      disable: [
        (date) => isReservationClosedDate(date, rules),
      ],
      onDayCreate: (_dObj, _dStr, _fp, dayElem) => {
        markHolidayDay(dayElem, dayElem.dateObj, rules);
      },
      onChange: (selectedDates, dateStr) => {
        syncReservationPeriod(periodSelect, noteEl, dateStr || '', rules);
      },
      onReady: (_selectedDates, dateStr) => {
        syncReservationPeriod(periodSelect, noteEl, dateStr || input.value || '', rules);
      },
    });

    instances.push({ fp, periodSelect, noteEl });

    if (input.value) {
      syncReservationPeriod(periodSelect, noteEl, input.value, rules);
    }
  });

  if (instances.length === 0) return;

  const resetAll = () => {
    instances.forEach(({ fp, periodSelect, noteEl }) => {
      resetReservationPair(fp, periodSelect, noteEl, rules);
    });
  };

  document.addEventListener('wpcf7reset', (e) => {
    const form = e.target;
    if (!(form instanceof Element)) return;
    if (!form.querySelector(selector)) return;
    resetAll();
  });

  document.addEventListener('wpcf7mailsent', (e) => {
    const form = e.target;
    if (!(form instanceof Element)) return;
    if (!form.querySelector(selector)) return;
    resetAll();
  });
}

export { addAutoFunc, numKeyFunc, confirmForm, reservationCalendarFunc };
